import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import find from 'lodash/find';

import BasePageFooter from './BasePageFooter';
import { FooterButtonContainer } from '../layout/MainLayout';
import { useUserContext } from '../contexts/UserContext';
import { useBackgroundContext } from '../contexts/BackgroundContext';
import { BasePageContent, ShadedBox } from './BasePage';
import ActionButton from '../components/buttons/ActionButton';
import ExpandingTextInput from '../components/ExpandingTextInput';
import OptionPill from '../components/OptionPill';
import MessageHistory from '../components/MessageHistory';
import { useEventCacheContext } from '../contexts/EventCacheContext';
import PageTitle from '../components/PageTitle';
import BasePageHeader from './BasePageHeader';
import { ErrorContainer } from '../components/forms/Input';
import { validateEmail } from '../util/email';
import { sendEventComment, updateAttendance } from '../api/ElkEventService';
import EventFormInput from '../components/EventForm/EventFormInput';

import { TAppElkAttendeeRole, TAppElkAttendeeStatus, TAppElkInvitee } from 'TProtocol/prototypes/events/messages';

const CHAR_LIMIT = 900;

const SendMessagePage = () => {
  const userContext = useUserContext();
  const eventCacheContext = useEventCacheContext();
  const backgroundContext = useBackgroundContext();
  const { eventId } = useParams();
  const formRef = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState<string>('');
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [messageId, setMessageId] = useState<string>(uuidv4);
  const [invalid, setInvalid] = useState(false);
  const [noRecipientError, setNoRecipientError] = useState(false);
  const [host, setHost] = useState<TAppElkInvitee>();
  const [sendToStatuses, setSendToStatuses] = useState<TAppElkAttendeeStatus[]>([
    TAppElkAttendeeStatus.YES
  ]);

  const navigate = useNavigate();
  const event = eventCacheContext.getEvent(eventId);

  useEffect(() => {
    if (event) {
      document.title = `Shine - Send Message - ${event.title}`;

      backgroundContext.setColors(
        { colors: event.colors, animation: event.animation }
      );
      const host = find(event.attendees, { role: TAppElkAttendeeRole.ORGANIZER });
      if (host) {
        setHost(host);
        if (host.email) {
          setEmail(host.email);
        }
      }
      if (email === '') {
        setEmail(userContext?.emails?.[0] ?? '')
      }
    }
  }, [event]);

  useEffect(() => {
    if (eventId !== undefined) {
      void eventCacheContext.fetchEvent({ eventId });
    }
  }, [event?.id, eventId]);

  const sendMessage = async () => {
    if (event?.id && host && host.email !== email) {
      host.email = email;
      //await updateAttendance(userContext, event.id, host, undefined, undefined, true);
    }

    void sendEventComment({
      userContext,
      eventId: event?.id ?? '',
      messageId,
      message,
      sendToStatuses,
      email: email !== '' ? email : undefined
    })

    navigate('/event/' + event?.id);
  };


  const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const email = e.currentTarget.value;
    if (email) {
      setInvalid(false);
    }
    setEmail(email);
  };

  const handleEmailChange = (e: string) => {
    if (e.length > 1) {
      setInvalid(false);
    }
    setEmail(e);
  };

  const onChange = (message: string) => {
    setMessage(message);
  };

  const onInvalid = (e: FormEvent) => {
    e.preventDefault();
    setInvalid(true);
  };

  const onBackClick = () => {
    //startUserEvent(UserEventType.BackFromTextBlastToEvent);
    navigate('/event/' + event?.id);
  };

  const validateForm = () => {
    let valid = true;

    if (validateEmail(email) || email === '') {
      setInvalidEmail(false);
    } else {
      setInvalidEmail(true);
      valid = false;
    }

    if (sendToStatuses.length === 0) {
      setNoRecipientError(true);
      valid = false;
    }

    if (message.length > CHAR_LIMIT) {
      valid = false;
    }
    return valid;
  };

  const onOptionClick = (status: TAppElkAttendeeStatus) => {
    const index = sendToStatuses.indexOf(status);
    if (index === -1) {
      sendToStatuses.push(status);
    } else {
      sendToStatuses.splice(index, 1);
    }
    setSendToStatuses([...sendToStatuses]);
    setNoRecipientError(false);
  };

  const onSendClick = () => {
    const validForm = validateForm();
    const validHtmlForm = formRef.current?.reportValidity();
    if (!validForm || !validHtmlForm) {
      return;
    }
    void sendMessage();
  };

  return <>
    <BasePageHeader/>
    <BasePageContent $showFooter={true}>
      <form ref={formRef} onSubmit={sendMessage}>
        <PageTitle onBackClick={onBackClick}>
          New Message
        </PageTitle>

        <ShadedBox>
          <PillContainer>
            <OptionPill label="Going"
                        color={event?.colors.highlightText ?? '#666'}
                        selected={sendToStatuses.indexOf(TAppElkAttendeeStatus.YES) !== -1}
                        onClick={() => onOptionClick(TAppElkAttendeeStatus.YES)}/>
            <OptionPill label="Declined"
                        color={event?.colors.highlightText ?? '#666'}
                        selected={sendToStatuses.indexOf(TAppElkAttendeeStatus.NO) !== -1}
                        onClick={() => onOptionClick(TAppElkAttendeeStatus.NO)}/>
            <OptionPill label="Not yet responded"
                        color={event?.colors.highlightText ?? '#666'}
                        selected={sendToStatuses.indexOf(TAppElkAttendeeStatus.INVITED) !== -1}
                        onClick={() => onOptionClick(TAppElkAttendeeStatus.INVITED)}/>
          </PillContainer>
          {noRecipientError ? <ErrorContainer>Please select at least one recipient group</ErrorContainer> : null}
          <EventFormInput label={'Your email'} value={email} onChange={handleEmailChange}/>
          {invalid ? <ErrorContainer>This is a required field</ErrorContainer> : null}
          {invalidEmail ? <ErrorContainer>Please enter a valid email</ErrorContainer> : null}

          <ExpandingTextInput
            label="Message to guests"
            value={message}
            textColor="var(--shine-text-color)"
            placeholder="-"
            onChange={onChange}
            charLimit={CHAR_LIMIT}
            required={true}
          />

          <ReminderText>
            Guests will receive either an email or a text message based on their preferences.
          </ReminderText>
        </ShadedBox>

        {event?.messages && event.messages.length > 0 && <ShadedBox>
          <MessageHistory messages={event.messages}/>
        </ShadedBox>}
      </form>
    </BasePageContent>
    <BasePageFooter height={110}>
      <FooterButtonContainer>
        {<ActionButton onClick={onSendClick} type="submit">Send</ActionButton>
        }
      </FooterButtonContainer>
    </BasePageFooter>
  </>;
};

export default SendMessagePage;

const PillContainer = styled.div`
  margin: 25px 0;
  display: flex;
  gap: 10px;
`;

const ReminderText = styled.div`
  font-style: italic;
  margin: 10px 0;
  font-size: 14px;
`;
